import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Image,
  ScrollView,
} from "react-native";
import {
  CreateResponsiveStyle,
  DEVICE_SIZES,
  useDeviceSize,
} from "rn-responsive-styles";
import { AppImages } from "../../utility/AppImages";
import { AppColors } from "../../utility/AppColors";
import { AppFontStyle } from "../../styles/AppFontStyle";
import { useLiveStream } from "../../context/LiveStreamContext";
import ChatIframe from "./chat-ui-frame";
import { toast } from "react-toastify";
import { STREAM_STATUS, STREAM_PLATFORMS, STREAM_ROLES } from "../../utility/LiveStreamSocket/LiveStreamConfig";
import { usePlatformAndChatUrlHandler } from "../../utility/hooks/usePlatformAndChatUrlHandler";
import { getEmbedDomain } from "../../utility/hooks/getEmbedDomain";

interface ChatMessage {
  commentId: string;
  commentData: string;
  userId: string;
  eventId: string;
  commentType: string;
  timestamp: number;
  profilePicture: string;
}

interface ChatPanelProps {
  variant: "event" | "participants";
  isExpandChat: boolean;
  setIsExpandChat: (value: boolean) => void;
  isChatLocked?: boolean;
}

const ChatRow = React.memo(({ item }: { item: ChatMessage }) => {
  const styles = useStyles();
  return (
    <View style={styles.chatRow}>
      <Image source={{ uri: item.profilePicture }} style={styles.avatar} />
      <View style={{ flexDirection: "row", gap: 7, alignItems: "center" }}>
        <Text
          style={[
            AppFontStyle.MEDIUM_ANYBODY_12,
            { color: AppColors.gray_BBB },
          ]}
        >
          {item.userId}
        </Text>
        <Text style={AppFontStyle.MEDIUM_ANYBODY_12}>
          {item.commentData}
        </Text>
      </View>
    </View>
  );
});

ChatRow.displayName = "ChatRow";

const ChatPanel: React.FC<ChatPanelProps> = ({
  variant,
  isExpandChat,
  setIsExpandChat,
  isChatLocked = false,
}) => {
  const styles = useStyles();
  const deviceSize = useDeviceSize();
  const scrollViewRef = useRef<ScrollView>(null);
  const [selectedTab, setSelectedTab] = useState(
    variant === "event" ? "KO Chat" : "Players"
  );
  const [newMessage, setNewMessage] = useState("");
  const [chatData, setChatData] = useState<ChatMessage[]>([]);
  const lastScrollTime = useRef(0);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const {
    organizerData,
    sendComment,
    currentEventId,
    userData,
    userRole,
    organiserStreamStatus,
    challengerStreamStatus,
  } = useLiveStream();

  // Defensive checks for organizerData
  if (!organizerData) {
    console.warn("ChatPanel: organizerData is not available yet");
  }

  const { streamDetails } = organizerData || {};
  const streamData = streamDetails?.[0];

  // For event variant, use platform handler
  const platformHandler = variant === "event" ? usePlatformAndChatUrlHandler({
    userRole,
    streamData,
    selectedTab,
    getEmbedDomain,
    STREAM_ROLES,
    STREAM_PLATFORMS,
  }) : null;

  // Define tabs based on variant
  const tabs = useMemo(() => {
    if (variant === "event" && platformHandler) {
      return platformHandler.getAvailablePlatforms();
    }
    return ["Players", "Shout caster", "Moderator"];
  }, [variant, platformHandler]);

  // Auto-select valid tab
  useEffect(() => {
    if (variant === "event") {
      if (tabs.length > 1 && !tabs.includes(selectedTab)) {
        setSelectedTab(tabs[1]);
      } else if (!tabs.includes(selectedTab)) {
        setSelectedTab("KO Chat");
      }
    }
  }, [tabs, selectedTab, variant]);

  // Memoized filtered chat data
  const filteredChatData = useMemo(
    () => {
      // For KO Chat, show messages with "Audience" comment type
      const targetCommentType = selectedTab === "KO Chat" ? "Audience" : selectedTab;
      return chatData.filter((chat) => chat.commentType === targetCommentType);
    },
    [chatData, selectedTab]
  );

  // Update chat data from context
  useEffect(() => {
    if (!organizerData?.chats) return;

    setChatData((prevChatData) => {
      const newChats = organizerData.chats.filter(
        (chat: any) =>
          !prevChatData.find((existing) => existing.commentId === chat.commentId)
      );
      if (newChats.length === 0) return prevChatData;

      return [...prevChatData, ...newChats].sort(
        (a, b) => a.timestamp - b.timestamp
      );
    });
  }, [organizerData?.chats]);

  // Handle scroll position detection
  const handleScroll = useCallback((event: any) => {
    const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
    const paddingToBottom = 20;
    const isBottom =
      layoutMeasurement.height + contentOffset.y >=
      contentSize.height - paddingToBottom;

    setIsAtBottom(isBottom);
    setShowScrollToBottom(!isBottom && filteredChatData.length > 0);
  }, [filteredChatData.length]);

  // Scroll to bottom function
  const scrollToBottom = useCallback(() => {
    if (!scrollViewRef.current || filteredChatData.length === 0) return;
    try {
      scrollViewRef.current.scrollToEnd({ animated: true });
      setShowScrollToBottom(false);
    } catch (e) {
      console.error("Error scrolling to bottom:", e);
    }
  }, [filteredChatData.length]);

  // Scroll to bottom when new messages arrive (only when user is at bottom)
  useEffect(() => {
    const now = Date.now();
    if (
      selectedTab === (variant === "event" ? "KO Chat" : "Players") &&
      filteredChatData.length > 0 &&
      isAtBottom &&
      now - lastScrollTime.current > 100
    ) {
      lastScrollTime.current = now;
      const timer = setTimeout(() => {
        scrollViewRef.current?.scrollToEnd({ animated: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [filteredChatData.length, selectedTab, variant, isAtBottom]);

  const handleSendMessage = useCallback(() => {
    if (newMessage.trim() === "") {
      if (variant === "event") {
        alert("Please enter a message.");
      } else {
        toast.info("Please enter a message.");
      }
      return;
    }

    // Defensive check: ensure we have necessary data to send message
    if (!currentEventId) {
      console.error("ChatPanel: Cannot send message - currentEventId is missing");
      toast.error("Chat connection not ready. Please try again.");
      return;
    }

    if (!userData?.username) {
      console.error("ChatPanel: Cannot send message - userData.username is missing");
      toast.error("User information not available. Please refresh.");
      return;
    }

    // Use "Audience" for KO Chat, otherwise use the selected tab
    const commentType = selectedTab === "KO Chat" ? "Audience" : selectedTab;

    const newChatEntry: ChatMessage = {
      commentData: newMessage.trim(),
      commentId: Date.now().toString(),
      userId: userData?.userID || userData?.username || "Unknown",
      eventId: currentEventId,
      commentType: commentType,
      timestamp: Date.now(),
      profilePicture: userData?.profilePicture || "https://via.placeholder.com/20",
    };

    console.log("Sending comment:", {
      username: userData?.username,
      eventId: currentEventId,
      message: newMessage.trim(),
      commentType,
    });

    sendComment(
      userData?.username || "Unknown",
      currentEventId,
      newMessage.trim(),
      commentType
    );

    setChatData((prev) => [...prev, newChatEntry].sort((a, b) => a.timestamp - b.timestamp));
    setNewMessage("");
  }, [newMessage, userData, currentEventId, selectedTab, sendComment, variant]);

  const handleTextChange = useCallback((text: string) => {
    if (text.endsWith("\n")) {
      handleSendMessage();
    } else {
      setNewMessage(text);
    }
  }, [handleSendMessage]);

  // Helper functions for event variant
  const isLive = useCallback((platform: string) => {
    if (variant !== "event") return false;
    if (platform === STREAM_PLATFORMS.YOUTUBE_CAPITALIZED || platform === STREAM_PLATFORMS.TWITCH_CAPITALIZED) {
      return userRole === STREAM_ROLES.ORGANISER
        ? organiserStreamStatus === STREAM_STATUS.LIVE
        : challengerStreamStatus === STREAM_STATUS.LIVE;
    }
    return false;
  }, [variant, userRole, organiserStreamStatus, challengerStreamStatus]);

  const isCompleted = useCallback(() => {
    if (variant !== "event") return false;
    return userRole === STREAM_ROLES.ORGANISER
      ? organiserStreamStatus === STREAM_STATUS.COMPLETED
      : challengerStreamStatus === STREAM_STATUS.COMPLETED;
  }, [variant, userRole, organiserStreamStatus, challengerStreamStatus]);

  const getStreamStatusMessage = useCallback(() => {
    if (variant === "event") {
      if (selectedTab === "KO Chat") {
        return filteredChatData.length === 0 ? "No messages yet" : null;
      }
      if (isCompleted()) {
        return "Stream Completed";
      }
      if (isLive(selectedTab)) {
        return "Stream is Live";
      }
      return "Stream Not Started";
    }
    return null;
  }, [variant, selectedTab, filteredChatData.length, isCompleted, isLive]);

  const isMobile = deviceSize === DEVICE_SIZES.XS || deviceSize === DEVICE_SIZES.SM;
  const canExpand = variant === "event" || (variant === "participants" && selectedTab === "Players");

  // Check if viewing platform chat (Twitch/YouTube) - in that case, hide input since platform has its own
  const isPlatformChat = variant === "event" &&
    (selectedTab === STREAM_PLATFORMS.YOUTUBE_CAPITALIZED ||
      selectedTab === STREAM_PLATFORMS.TWITCH_CAPITALIZED);

  const canSendMessage = !isChatLocked &&
    !isPlatformChat &&
    (variant === "event" || selectedTab === "Players");

  return (
    <View style={styles.container}>
      {/* Header and Tabs */}
      <View style={{ paddingVertical: 7, paddingHorizontal: 10 }}>
        <Text style={[AppFontStyle.BOLD_ANYBODY_12, { marginBottom: 6 }]}>
          {variant === "event" ? "Live Event Chat" : "Live Participants Chat"}
        </Text>
        <View style={styles.tabsContainer}>
          {tabs.map((tab, index) => (
            <TouchableOpacity
              key={index}
              style={[
                styles.tabButton,
                selectedTab === tab && styles.selectedTab,
              ]}
              onPress={() => setSelectedTab(tab)}
            >
              <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { textAlign: "center" }]}>
                {tab}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Chat Container - Height increases when input is hidden for platform chat */}
      <View style={[styles.chatContainer, isPlatformChat && { height: 420 }]}>
        {/* Scroll to Bottom Button */}
        {showScrollToBottom && !isChatLocked && (
          <TouchableOpacity
            onPress={scrollToBottom}
            style={{
              position: "absolute",
              bottom: 10,
              right: 10,
              backgroundColor: "rgba(220, 38, 38, 0.9)",
              borderRadius: 20,
              width: 40,
              height: 40,
              justifyContent: "center",
              alignItems: "center",
              shadowColor: "#000",
              shadowOffset: { width: 0, height: 2 },
              shadowOpacity: 0.3,
              shadowRadius: 4,
              elevation: 5,
              zIndex: 1000,
            }}
          >
            <Text style={{ color: "white", fontSize: 20, fontWeight: "bold" }}>
              ↓
            </Text>
          </TouchableOpacity>
        )}

        {isChatLocked ? (
          <View style={styles.chatLockedContainer}>
            <Image
              source={AppImages.icLock}
              style={styles.lockIcon}
              resizeMode="contain"
            />
            <Text style={[AppFontStyle.BOLD_ANYBODY_16, { color: AppColors.white, marginTop: 15 }]}>
              Chat Lock
            </Text>
            <Text style={[AppFontStyle.REGULAR_ANYBODY_12, { color: AppColors.gray_BBB, marginTop: 10, textAlign: "center", paddingHorizontal: 20 }]}>
              {variant === "event" ? "KO Chat" : "Participants chat"} will appear here when the challenge is accepted
            </Text>
          </View>
        ) : (variant === "event" && selectedTab === "KO Chat") ||
          (variant === "participants" && selectedTab === "Players") ? (
          <ScrollView
            ref={scrollViewRef}
            style={[
              isMobile && {
                height: isExpandChat ? (variant === "event" ? 365 : 500) : 114,
              },
            ]}
            contentContainerStyle={{
              paddingBottom: 10,
              flexGrow: 1,
              justifyContent: "flex-end",
            }}
            showsVerticalScrollIndicator={false}
            onScroll={handleScroll}
            scrollEventThrottle={16}
          >
            {filteredChatData.length === 0 && variant === "event" && (
              <View style={styles.chatPlaceholder}>
                <Text style={[AppFontStyle.MEDIUM_ANYBODY_12, { color: AppColors.gray_BBB }]}>
                  {getStreamStatusMessage()}
                </Text>
              </View>
            )}
            {filteredChatData.map((item) => (
              <ChatRow key={item.commentId} item={item} />
            ))}
          </ScrollView>
        ) : variant === "event" && isLive(selectedTab) && platformHandler ? (
          <ChatIframe
            platform={selectedTab}
            liveChatUrl={platformHandler.getLiveChatUrl()}
            isLive={isLive(selectedTab)}
          />
        ) : variant === "participants" && selectedTab !== "Players" ? (
          <View style={styles.overlay}>
            <Text style={[AppFontStyle.BOLD_ANYBODY_16, { color: AppColors.white }]}>
              {selectedTab}
            </Text>
            <Text style={[AppFontStyle.BOLD_ANYBODY_24, { color: "#FACC15" }]}>
              Coming Soon
            </Text>
          </View>
        ) : (
          <View style={styles.chatPlaceholder}>
            <Text style={[AppFontStyle.MEDIUM_ANYBODY_12, { color: AppColors.gray_BBB }]}>
              {getStreamStatusMessage() || "No messages yet"}
            </Text>
          </View>
        )}
      </View>

      {/* Input Container - Hidden for platform chats (Twitch/YouTube have their own input) */}
      {!isPlatformChat && (
        <View
          style={[
            styles.inputContainer,
            !canSendMessage && styles.inputContainerDisabled,
          ]}
        >
          {/* Expand icon removed as per Bug #16 */}
          <TextInput
            style={[
              styles.textInput,
              !canSendMessage && styles.textInputDisabled,
            ]}
            placeholder="Send a message"
            placeholderTextColor={AppColors.grayColor_484848}
            value={newMessage}
            onChangeText={handleTextChange}
            onSubmitEditing={handleSendMessage}
            returnKeyType="send"
            blurOnSubmit={false}
            editable={canSendMessage}
            pointerEvents={canSendMessage ? "auto" : "none"}
          />
          <TouchableOpacity
            onPress={handleSendMessage}
            style={[
              styles.sendButton,
              !canSendMessage && { opacity: 0.5 },
            ]}
            disabled={!canSendMessage || newMessage.trim() === ""}
          >
            <Image source={AppImages.icSendIcon} style={styles.sendIcon} />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const useStyles = CreateResponsiveStyle(
  {
    container: {
      backgroundColor: AppColors.grayColor_282828,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: AppColors.purple_453B50,
    },
    tabsContainer: {
      flexDirection: "row",
      gap: 6,
    },
    tabButton: {
      padding: 8,
      borderRadius: 5,
      flex: 0.33,
      backgroundColor: AppColors.blackTrans_000000a8,
    },
    selectedTab: {
      backgroundColor: AppColors.sidebarBorder_374151,
    },
    chatContainer: {
      height: 365,
      paddingHorizontal: 12,
      backgroundColor: AppColors.sidebarBackground_111827,
      position: "relative",
    },
    chatRow: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 8,
    },
    avatar: {
      width: 20,
      height: 20,
      borderRadius: 19,
      marginRight: 5,
    },
    inputContainer: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 15,
      paddingHorizontal: 20,
    },
    inputContainerDisabled: {
      opacity: 0.5,
    },
    textInput: {
      flex: 1,
      color: AppColors.white,
      paddingVertical: 8,
      paddingHorizontal: 10,
      borderRadius: 17,
      backgroundColor: AppColors.black_18151b,
    },
    textInputDisabled: {
      backgroundColor: AppColors.gray_1E1E1E,
    },
    sendButton: {
      marginLeft: 10,
      position: "absolute",
      right: 35,
    },
    sendIcon: {
      width: 20,
      height: 20,
    },
    expandIcon: {
      width: 24,
      height: 24,
      resizeMode: "contain",
      marginRight: 12,
    },
    chatPlaceholder: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
    },
    overlay: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: "rgba(90, 90, 90, 0.2)",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 10,
    },
    chatLockedContainer: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      paddingHorizontal: 20,
    },
    lockIcon: {
      width: 60,
      height: 60,
      tintColor: AppColors.white,
    },
  },
  {
    [DEVICE_SIZES.LG]: {},
    [DEVICE_SIZES.MD]: {},
    [DEVICE_SIZES.SM]: {},
    [DEVICE_SIZES.XS]: {},
  }
);

export default ChatPanel;
